import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { pageTitle } from '../../helper';
import Cta from '../Cta';
import PageHeading from '../PageHeading';
import Div from '../Div';
import Spacing from '../Spacing';
import { jobs } from './CareersPage';

export default function CareerDetailsPage() {
  const { jobId } = useParams();
  const job = jobs.find((j) => j.slug === jobId);

  pageTitle(job ? job.title : 'Career Details');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [jobId]);

  if (!job) {
    return <Navigate to="/careers" replace />;
  }

  const mailtoLink = `mailto:hr@codewus.ae?subject=${encodeURIComponent(`Application: ${job.title}`)}`;

  const currentIndex = jobs.indexOf(job);
  const similarJobs = jobs.filter((j, i) => i !== currentIndex).slice(0, 4);

  return (
    <>
      <PageHeading
        title="Career Details"
        bgSrc="/images/contact_hero_bg.jpeg"
        pageLinkText={job.title}
      />
      <Spacing lg="150" md="80" />
      <Div className="container">
        <Div className="row">
          {/* Left column - job details */}
          <Div className="col-lg-8">
            <h2 className="cs-font_38 cs-m0" style={{ marginBottom: 20 }}>
              {job.title}
            </h2>
            <Div style={{ display: 'flex', gap: 12, marginBottom: 30, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 14,
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#ff4a17',
                }}
              >
                {job.level}
              </span>
              <span
                style={{
                  fontSize: 14,
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#999',
                }}
              >
                {job.location} • {job.employmentType}
              </span>
              <span
                style={{
                  fontSize: 14,
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#999',
                }}
              >
                {job.salary}
              </span>
              <span
                style={{
                  fontSize: 14,
                  padding: '6px 16px',
                  borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#999',
                }}
              >
                {job.experience}
              </span>
            </Div>

            {/* About */}
            <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 15, textTransform: 'uppercase' }}>
              About This Position
            </h3>
            <p style={{ color: '#999', lineHeight: 1.8, marginBottom: 30 }}>
              {job.summary}
            </p>

            {/* Responsibilities */}
            <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 15, textTransform: 'uppercase' }}>
              Responsibilities
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: 30 }}>
              {job.responsibilities.map((item, i) => (
                <li key={i} style={{ marginBottom: 10, paddingLeft: 20, position: 'relative', color: '#999', lineHeight: 1.7 }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ff4a17' }}>•</span>
                  {item}
                </li>
              ))}
            </ul>

            {/* Requirements */}
            <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 15, textTransform: 'uppercase' }}>
              Requirements
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: 30 }}>
              {job.requirements.map((item, i) => (
                <li key={i} style={{ marginBottom: 10, paddingLeft: 20, position: 'relative', color: '#999', lineHeight: 1.7 }}>
                  <span style={{ position: 'absolute', left: 0, color: '#ff4a17' }}>•</span>
                  {item}
                </li>
              ))}
            </ul>

            {/* Nice to Have */}
            {job.niceToHave && job.niceToHave.length > 0 && (
              <>
                <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 15, textTransform: 'uppercase' }}>
                  Nice to Have
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: 30 }}>
                  {job.niceToHave.map((item, i) => (
                    <li key={i} style={{ marginBottom: 10, paddingLeft: 20, position: 'relative', color: '#999', lineHeight: 1.7 }}>
                      <span style={{ position: 'absolute', left: 0, color: '#ff4a17' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Apply Button */}
            <a href={mailtoLink} className="cs-btn cs-style1" style={{ display: 'inline-flex' }}>
              <span>Apply Now</span>
              <Icon icon="bi:arrow-right" />
            </a>
          </Div>

          {/* Right column - sidebar */}
          <Div className="col-lg-4">
            <Spacing lg="0" md="40" />
            <Div
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 15,
                padding: '30px',
              }}
            >
              <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 20 }}>
                Job Summary
              </h3>
              <Div style={{ marginBottom: 15 }}>
                <span className="cs-accent_color" style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>Level</span>
                <span style={{ color: '#ccc' }}>{job.level}</span>
              </Div>
              <Div style={{ marginBottom: 15 }}>
                <span className="cs-accent_color" style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>Location</span>
                <span style={{ color: '#ccc' }}>{job.location}</span>
              </Div>
              <Div style={{ marginBottom: 15 }}>
                <span className="cs-accent_color" style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>Type</span>
                <span style={{ color: '#ccc' }}>{job.employmentType}</span>
              </Div>
              <Div style={{ marginBottom: 15 }}>
                <span className="cs-accent_color" style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>Salary</span>
                <span style={{ color: '#ccc' }}>{job.salary}</span>
              </Div>
              <Div style={{ marginBottom: 0 }}>
                <span className="cs-accent_color" style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>Experience</span>
                <span style={{ color: '#ccc' }}>{job.experience}</span>
              </Div>
            </Div>

            <Spacing lg="30" md="30" />

            {/* Similar positions */}
            <Div
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 15,
                padding: '30px',
              }}
            >
              <h3 className="cs-font_22 cs-m0" style={{ marginBottom: 20 }}>
                Other Positions
              </h3>
              {similarJobs.map((sj, i) => (
                <Div key={i} style={{ marginBottom: i < similarJobs.length - 1 ? 15 : 0 }}>
                  <Link
                    to={`/careers/${sj.slug}`}
                    className="cs-text_btn"
                    style={{ fontSize: 15 }}
                  >
                    <span>{sj.title}</span>
                    <Icon icon="bi:arrow-right" />
                  </Link>
                  <span style={{ fontSize: 13, color: '#666', display: 'block', marginTop: 3 }}>
                    {sj.location} • {sj.level}
                  </span>
                </Div>
              ))}
            </Div>
          </Div>
        </Div>
      </Div>
      <Spacing lg="150" md="80" />
      <Cta
        title="info@codewus.ae"
        bgSrc="/images/cta_bg_2.jpeg"
        variant="rounded-0"
      />
    </>
  );
}
